import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { Company } from '@evogard/contract';
import {
  loadFailureMessage,
  type CompanyCategory,
  type CompanyType,
} from '@evogard/domain';
import type { IconName } from '@evogard/ui';
import { api, ok } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';

/* O nome de cada tipo mora num lugar só — o formulário de beneficiário lê o mesmo. */
export { COMPANY_TYPE_LABELS } from '../../../components/beneficiary/labels.js';

/**
 * Toda conversa da interna com o cadastro de empresas de apoio passa por aqui
 * — `companies`, que a tela separa em **Prestadores** e
 * **Instituições** (decidido em 25/09; o banco não sabe dos grupos).
 *
 * As mesmas regras de consulta de `third-parties/useThirdParties.ts`: a praça
 * na chave, o primeiro segmento é `sinistros` (o id do módulo no menu), e toda
 * escrita invalida a lista — inclusive a do formulário de beneficiário, que
 * procura a empresa por aqui.
 */

export type { Company };

const ROOT = ['sinistros', 'empresas'] as const;
export const COMPANIES_KEY = ROOT;

export function useCompanyPermissions() {
  const { data: context } = useSessionContext();
  return {
    canView: can(context, 'claims.companies', 'ver'),
    canCreate: can(context, 'claims.companies', 'criar'),
    canEdit: can(context, 'claims.companies', 'editar'),
  };
}

/** O que cada tela diz de si — o grupo decide o nome, o ícone e a frase. */
export interface CategoryCopy {
  title: string;
  /** "prestador", "instituição" — para "Novo prestador", "1 instituição". */
  singular: string;
  plural: string;
  /** "Novo prestador" / "Nova instituição". */
  newLabel: string;
  /** "Nenhum prestador" / "Nenhuma instituição". */
  none: string;
  /** "Nenhum prestador ativo" / "Nenhuma instituição ativa" — a lista sem filtro esconde o arquivado. */
  noneActive: string;
  /** "cadastrados" / "cadastradas" — concorda com o plural. */
  registered: string;
  icon: IconName;
  description: string;
  path: '/companies' | '/institutions';
}

// @valor
export const CATEGORY_COPY: Readonly<Record<CompanyCategory, CategoryCopy>> = {
  prestadores: {
    title: 'Prestadores',
    singular: 'prestador',
    plural: 'prestadores',
    newLabel: 'Novo prestador',
    none: 'Nenhum prestador',
    noneActive: 'Nenhum prestador ativo',
    registered: 'cadastrados',
    icon: 'cracha',
    description:
      'Quem presta serviço à casa — oficinas, prestadores de serviço e fornecedores ' +
      'de peças. Pessoa física ou jurídica.',
    path: '/companies',
  },
  instituicoes: {
    title: 'Instituições',
    singular: 'instituição',
    plural: 'instituições',
    newLabel: 'Nova instituição',
    none: 'Nenhuma instituição',
    noneActive: 'Nenhuma instituição ativa',
    registered: 'cadastradas',
    icon: 'bandeira',
    description:
      'Quem recebe pagamento da Indenização — instituições financeiras (a quitação do ' +
      'financiamento) e órgãos públicos. Só nome e CNPJ.',
    path: '/institutions',
  },
};

function useTenantId() {
  const { data: context } = useSessionContext();
  return context?.currentTenant.id;
}

/** A lista de um grupo, com busca por nome ou documento, paginada no servidor. */
export function useCompanySearch(
  category: CompanyCategory,
  options: {
    search?: string;
    type?: CompanyType;
    includeArchived?: boolean;
    page?: number;
    perPage?: number;
  } = {},
) {
  const tenantId = useTenantId();
  const { search = '', type, includeArchived = false, page = 1, perPage = 10 } = options;
  const term = search.trim();

  return useQuery({
    queryKey: [
      ...ROOT,
      'lista',
      tenantId,
      category,
      term,
      type ?? null,
      includeArchived,
      page,
      perPage,
    ],
    queryFn: async () => {
      const r = await api.claims.listCompanies({
        query: {
          page,
          perPage,
          category,
          ...(term ? { search: term } : {}),
          ...(type ? { type } : {}),
          ...(includeArchived ? { includeArchived: 'true' as const } : {}),
        },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, CATEGORY_COPY[category].plural));
      }
      return r.body;
    },
    /* segura a página anterior só dentro da mesma praça */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === tenantId ? keepPreviousData(previous) : undefined,
  });
}

export interface CompanyBody {
  type: CompanyType;
  name: string;
  documentType: 'cpf' | 'cnpj';
  document: string;
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
}

export function useCompanyMutations() {
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ROOT });
    /* o formulário de beneficiário procura empresa pela mesma rota */
    void client.invalidateQueries({ queryKey: ['sinistros', 'companhia'] });
  };

  const create = useMutation({
    mutationFn: async (body: CompanyBody) => {
      const r = await api.claims.createCompany({ body });
      return ok(r, 201, 'Não foi possível cadastrar.');
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: Partial<CompanyBody> }) => {
      const r = await api.claims.updateCompany({ params: { id }, body });
      return ok(r, 200, 'Não foi possível salvar a correção.');
    },
    onSuccess: invalidate,
  });

  const archive = useMutation({
    mutationFn: async ({ id, archived }: { id: number; archived: boolean }) => {
      const r = await api.claims.updateCompany({ params: { id }, body: { archived } });
      return ok(
        r,
        200,
        archived ? 'Não foi possível arquivar.' : 'Não foi possível reativar.',
      );
    },
    onSuccess: invalidate,
  });

  return { create, update, archive };
}
